import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import DataTable from "../components/DataTable";
import PageHeader from "../components/PageHeader";
import { Badge, statusVariant } from "../components/Badge";
import { Check, X, Plus, FileDown, Loader2, RefreshCw, AlertOctagon, Info, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fmtBDT } from "../lib/format";
import NumberInput from "../../components/ui/NumberInput";

export default function Withdraws({ onlyPending = false }) {
  const [rows, setRows] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterMethod, setFilterMethod] = useState("All");
  const [action, setAction] = useState(null);
  const [note, setNote] = useState("");
  const [txnRef, setTxnRef] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ player_user_id: "", amount: "", payment_method: "bKash", note: "", txn_ref: "" });
  const [gateways, setGateways] = useState([]);
  const [overrideGwId, setOverrideGwId] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [forceStatus, setForceStatus] = useState("");

  const load = async () => {
    setLoading(true);
    // "Pending" tab now includes both pending and failed-retry rows so admin
    // can re-approve a gateway-failed withdrawal from the same screen.
    const { data } = await api.get("/withdrawals");
    const list = onlyPending
      ? (data || []).filter((r) => r.status === "pending" || r.status === "failed" || r.status === "processing")
      : data;
    setRows(list);
    setSelected(new Set());
    setLoading(false);
  };
  const loadGateways = async () => {
    try {
      const { data } = await api.get("/site/admin/gateways");
      setGateways((data?.items || []).filter((g) => g.enabled));
    } catch (_e) { /* gateways list optional */ }
  };
  useEffect(() => { load(); loadGateways(); /* eslint-disable-next-line */ }, [onlyPending]);

  // When opening the approve modal, default the override-gateway dropdown to
  // the row's existing gateway_id (if any) so the admin sees "currently using X".
  useEffect(() => {
    if (action) {
      setOverrideGwId(action.row?.gateway_id || "");
      setForceStatus("");
      setAdvancedOpen(false);
    }
  }, [action]);

  const eligibleGateways = useMemo(() => {
    if (!action?.row) return [];
    const m = (action.row.payment_method || action.row.method || "").toLowerCase();
    return gateways.filter((g) =>
      Array.isArray(g.methods) ? g.methods.map((x) => (x || "").toLowerCase()).includes(m) : false
    );
  }, [gateways, action]);

  const methods = useMemo(() => ["All", ...Array.from(new Set(rows.map(r => r.payment_method).filter(Boolean)))], [rows]);
  const filtered = filterMethod === "All" ? rows : rows.filter(r => r.payment_method === filterMethod);

  const confirmAction = async () => {
    try {
      const payload = { status: action.status, note, txn_ref: txnRef };
      // For Approve, pass admin-chosen gateway override (if different from row's default).
      if (action.status === "approved" && overrideGwId) {
        payload.gateway_id = overrideGwId;
      }
      // Advanced: force_status bypasses gateway entirely.
      if (forceStatus) {
        payload.force_status = forceStatus;
      }
      const { data } = await api.patch(`/withdrawals/${action.row.id}`, payload);
      toast.success(`Withdrawal → ${data?.status || action.status}${data?.forced ? " (forced)" : ""}`);
      setAction(null); setNote(""); setTxnRef(""); setOverrideGwId(""); setForceStatus("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    }
  };

  const bulkAction = async (status) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`${status === "approved" ? "Approve" : "Reject"} ${ids.length} withdrawal(s)?`)) return;
    const results = await Promise.allSettled(ids.map(id => api.patch(`/withdrawals/${id}`, { status })));
    const ok = results.filter(r => r.status === "fulfilled").length;
    toast.success(`${ok}/${ids.length} withdrawals ${status}`);
    load();
  };

  const createManual = async (e) => {
    e.preventDefault();
    try {
      await api.post("/withdrawals", { ...form, amount: Number(form.amount), auto_approve: true });
      toast.success("Manual withdrawal debited");
      setShowCreate(false);
      setForm({ player_user_id: "", amount: "", payment_method: "bKash", note: "", txn_ref: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  const toggleSel = (id) => { const n = new Set(selected); if (n.has(id)) n.delete(id); else n.add(id); setSelected(n); };
  const toggleAll = () => {
    const eligible = filtered.filter(r => r.status === "pending" || r.status === "failed");
    if (selected.size === eligible.length && eligible.length > 0) setSelected(new Set());
    else setSelected(new Set(eligible.map(r => r.id)));
  };

  const forceFail = async (row) => {
    if (!window.confirm(`Force-mark withdrawal #${(row.id || "").slice(0, 8)} as FAILED?\n\nUse this ONLY if you have confirmed via the gateway dashboard or bank statement that the money did NOT go out. The player will be refunded and you can retry Approve.`)) return;
    try {
      await api.post(`/withdrawals/${row.id}/force-fail`);
      toast.success("Withdrawal marked failed — player refunded.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Force-fail failed");
    }
  };

  const forceComplete = async (row) => {
    const shortId = (row.id || "").slice(0, 8);
    const ref = window.prompt(
      `Force-mark withdrawal #${shortId} as COMPLETED?\n\n` +
      `Use this ONLY if you have confirmed in the gateway dashboard / bank app that the money WAS sent to the player.\n\n` +
      `Player: ${row.player_name}\nAmount: ${row.amount} ${row.currency || "BDT"}\nAccount: ${row.payer_account || row.wallet_address || "-"}\n\n` +
      `Enter the gateway TXN reference (optional, helps audit):`,
      row.gateway_order_no || row.txn_ref || ""
    );
    if (ref === null) return; // user cancelled
    if (!window.confirm(`Confirm: mark this withdrawal COMPLETED?\nThis CANNOT be undone. The player will NOT be refunded.`)) return;
    try {
      await api.patch(`/withdrawals/${row.id}`, {
        status: "approved",
        force_status: "completed",
        note: `Manually completed by admin (gateway sent money, callback missed). Ref: ${ref || "n/a"}`,
        txn_ref: ref || undefined,
      });
      toast.success("Withdrawal marked completed.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Force-complete failed");
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (onlyPending) params.set("status", "pending");
      const { data } = await api.get(`/withdrawals/download.pdf?${params.toString()}`, { responseType: "blob" });
      const ts = new Date().toISOString().slice(0, 16).replace("T", "-").replace(":", "");
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `site-${onlyPending ? "pending" : "all"}-withdrawals-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="withdraws-page">
      <PageHeader
        title={onlyPending ? "Pending Withdrawals" : "All Withdrawals"}
        subtitle={onlyPending ? "Review, filter, approve or reject withdrawal requests" : "Full withdrawal ledger with filters and manual entry"}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={downloadPdf}
              disabled={downloading}
              className="h-9 px-3 inline-flex items-center gap-1.5 bg-white border border-gray-200 hover:border-emerald-300 disabled:opacity-50 text-gray-700 text-sm rounded-sm"
              data-testid="withdraw-download-pdf"
            >
              {downloading ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Download PDF
            </button>
            <button onClick={() => setShowCreate(true)} className="h-9 px-3 bg-emerald-500 hover:bg-emerald-600 text-white text-sm rounded-sm inline-flex items-center gap-1.5" data-testid="withdraw-manual-btn">
              <Plus size={14} /> Manual withdrawal
            </button>
          </div>
        }
      />

      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Method</span>
          {methods.map(m => (
            <button key={m} onClick={() => setFilterMethod(m)} className={`h-8 px-3 text-xs rounded-sm border ${filterMethod === m ? "bg-gray-900 text-white border-gray-900" : "bg-white border-gray-200 text-gray-700 hover:border-emerald-300"}`} data-testid={`withdraw-filter-${m}`}>
              {m}
            </button>
          ))}
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2" data-testid="bulk-actions">
            <span className="text-xs text-gray-500">{selected.size} selected</span>
            <button onClick={() => bulkAction("approved")} className="h-8 px-3 text-xs bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm inline-flex items-center gap-1" data-testid="bulk-approve"><Check size={12} /> Approve</button>
            <button onClick={() => bulkAction("rejected")} className="h-8 px-3 text-xs bg-red-500 hover:bg-red-600 text-white rounded-sm inline-flex items-center gap-1" data-testid="bulk-reject"><X size={12} /> Reject</button>
          </div>
        )}
      </div>

      <DataTable
        testId="withdraws-table"
        loading={loading}
        rows={filtered}
        columns={[
          {
            key: "sel", mobileHide: true, label: (
              <input type="checkbox" onChange={toggleAll} checked={filtered.filter(r => r.status === "pending").length > 0 && selected.size === filtered.filter(r => r.status === "pending").length} data-testid="bulk-select-all" />
            ), render: (r) => r.status === "pending" ? (
              <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} onClick={(e) => e.stopPropagation()} data-testid={`select-${r.id}`} />
            ) : null
          },
          { key: "txn_id", label: "Txn ID" },
          {
            key: "player",
            label: "Player",
            searchKeys: ["player_user_id", "player_id", "player_name"],
            render: (r) => (
              <div className="leading-tight">
                <div className="text-sm text-gray-900 font-medium truncate max-w-[180px]" title={r.player_user_id || r.player_name || ""}>
                  {r.player_user_id || r.player_name || "—"}
                </div>
                {r.player_id ? (
                  <div className="text-[11px] text-gray-500 font-mono truncate max-w-[180px]" title={`Player ID: ${r.player_id}`}>
                    ID: {r.player_id}
                  </div>
                ) : null}
              </div>
            ),
          },
          { key: "amount", label: "Amount", align: "right", render: (r) => fmtBDT(r.amount) },
          { key: "payment_method", label: "Method" },
          {
            key: "payer_account",
            label: "Account / Number",
            render: (r) => {
              const acc = r.payer_account || r.wallet_address || "";
              if (!acc) return <span className="text-gray-400 text-xs">—</span>;
              const isCrypto = !!r.wallet_address;
              return (
                <span
                  className={`font-mono text-xs ${isCrypto ? "text-purple-700" : "text-gray-900"} break-all`}
                  title={acc}
                  data-testid={`wd-account-${r.id}`}
                >
                  {isCrypto && r.crypto_network ? (
                    <span className="text-[10px] bg-purple-100 text-purple-700 px-1 mr-1 rounded">{r.crypto_network}</span>
                  ) : null}
                  {acc.length > 24 ? `${acc.slice(0, 10)}…${acc.slice(-8)}` : acc}
                </span>
              );
            },
          },
          {
            key: "status",
            label: "Status",
            render: (r) => {
              const reason = r.failed_reason || r.admin_note;
              if (r.status === "failed") {
                return (
                  <span className="inline-flex items-center gap-1" title={reason || "Gateway payout failed — click Approve to retry"}>
                    <Badge variant="warning">failed · retry</Badge>
                    {reason ? <Info size={12} className="text-amber-500" /> : null}
                  </span>
                );
              }
              if (r.status === "processing") {
                return (
                  <span className="inline-flex items-center gap-1" title="Gateway payout in flight — waiting for callback">
                    <Badge variant="info">processing</Badge>
                    <Loader2 size={11} className="animate-spin text-blue-500" />
                  </span>
                );
              }
              return <Badge variant={statusVariant(r.status)}>{r.status}</Badge>;
            },
          },
          { key: "created_at", label: "Date", render: (r) => new Date(r.created_at).toLocaleString("en-GB") },
        ]}
        actions={(row) => {  // eslint-disable-line react/no-unstable-nested-components
          if (row.status === "pending" || row.status === "failed") {
            const isRetry = row.status === "failed";
            return (
              <div className="flex flex-wrap items-center gap-1 justify-end">
                <button
                  onClick={() => { setAction({ row, status: "approved" }); setNote(""); setTxnRef(""); }}
                  className={`h-8 px-2 inline-flex items-center gap-1 text-xs rounded-sm ${isRetry ? "border border-amber-300 text-amber-700 hover:bg-amber-50" : "border border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}
                  data-testid={`withdraw-approve-${row.id}`}
                  title={isRetry ? `Retry gateway payout. Previous error: ${row.failed_reason || row.admin_note || "unknown"}` : "Approve withdrawal"}
                >
                  {isRetry ? <><RefreshCw size={13} /> Retry</> : <><Check size={13} /> Approve</>}
                </button>
                <button
                  onClick={() => { setAction({ row, status: "rejected" }); setNote(""); setTxnRef(""); }}
                  className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-red-200 text-red-700 rounded-sm hover:bg-red-50"
                  data-testid={`withdraw-reject-${row.id}`}
                >
                  <X size={13} /> Reject
                </button>
              </div>
            );
          }
          if (row.status === "processing") {
            return (
              <div className="flex items-center gap-1 justify-end">
                <button
                  onClick={() => forceComplete(row)}
                  className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-emerald-300 text-emerald-700 rounded-sm hover:bg-emerald-50"
                  data-testid={`withdraw-force-complete-${row.id}`}
                  title="Force-mark this stuck withdrawal as completed (only if gateway confirms money WAS sent to player)"
                >
                  <CheckCircle2 size={12} /> Force-complete
                </button>
                <button
                  onClick={() => forceFail(row)}
                  className="h-8 px-2 inline-flex items-center gap-1 text-xs border border-amber-300 text-amber-700 rounded-sm hover:bg-amber-50"
                  data-testid={`withdraw-force-fail-${row.id}`}
                  title="Force-mark this stuck withdrawal as failed (only if gateway confirms money did not go out)"
                >
                  <AlertOctagon size={12} /> Force-fail
                </button>
              </div>
            );
          }
          return null;
        }}
      />

      {action && (
        <Modal title={`${action.status === "approved" ? (action.row.status === "failed" ? "Retry gateway payout" : "Approve withdrawal") : "Reject withdrawal"}`} onClose={() => setAction(null)}>
          <div className="text-sm text-gray-600 mb-3">
            <div>Player: <b>{action.row.player_name}</b></div>
            <div>Amount: <b>{fmtBDT(action.row.amount)}</b></div>
            <div>Method: {action.row.payment_method}</div>
            {(action.row.payer_account || action.row.wallet_address) && (
              <div data-testid="action-payer-account" className="mt-1">
                {action.row.wallet_address ? (
                  <>
                    Wallet address{action.row.crypto_network ? ` (${action.row.crypto_network})` : ""}:{" "}
                    <b className="font-mono text-xs break-all text-purple-700">{action.row.wallet_address}</b>
                  </>
                ) : (
                  <>
                    Account / Number:{" "}
                    <b className="font-mono text-gray-900">{action.row.payer_account}</b>
                  </>
                )}
              </div>
            )}
            {action.status === "approved" && action.row.status === "failed" && (action.row.failed_reason || action.row.admin_note) && (
              <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="retry-prev-reason">
                <b className="block mb-1 uppercase tracking-wide text-[10px]">Previous gateway error</b>
                {action.row.failed_reason || action.row.admin_note}
              </div>
            )}
          </div>
          {action.status === "approved" && (
            <Field label="Transaction reference (bank/bkash trxid)" value={txnRef} onChange={setTxnRef} testId="action-txnref" />
          )}
          {action.status === "approved" && eligibleGateways.length > 0 && (
            <div className="mt-3">
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                Payout gateway {action.row.status === "failed" ? "(try a different one)" : ""}
              </label>
              <select
                value={overrideGwId}
                onChange={(e) => setOverrideGwId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm bg-white"
                data-testid="action-gateway-override"
              >
                <option value="">— Use system default —</option>
                {eligibleGateways.map((g) => (
                  <option key={g.id} value={g.id}>
                    {(g.label || g.provider || "").toString()} · {(g.provider || "").toUpperCase()}
                    {g.id === action.row.gateway_id ? " (current)" : ""}
                  </option>
                ))}
              </select>
              <div className="text-[10.5px] text-gray-500 mt-1">
                Eligible gateways for method <b>{action.row.payment_method}</b>. Choose another to retry payout via a different provider.
              </div>
            </div>
          )}
          <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1 mt-3">Admin note (optional)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm" data-testid="action-note" />

          {/* Advanced: manual status override */}
          <div className="mt-3 border-t border-gray-100 pt-3">
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold hover:text-gray-700"
              data-testid="action-advanced-toggle"
            >
              {advancedOpen ? "▾" : "▸"} Advanced — manual status override
            </button>
            {advancedOpen && (
              <div className="mt-2 p-2 rounded bg-gray-50 border border-gray-200">
                <label className="block text-[10.5px] text-gray-600 mb-1">
                  Force a status directly (skips gateway). Use only for offline reconciliation.
                </label>
                <select
                  value={forceStatus}
                  onChange={(e) => setForceStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm bg-white"
                  data-testid="action-force-status"
                >
                  <option value="">— Don&apos;t override —</option>
                  <option value="pending">pending</option>
                  <option value="processing">processing</option>
                  <option value="approved">approved</option>
                  <option value="completed">completed</option>
                  <option value="failed">failed</option>
                  <option value="rejected">rejected</option>
                </select>
                {forceStatus && (
                  <div className="text-[10.5px] text-amber-700 mt-2 flex items-start gap-1">
                    <AlertOctagon size={12} className="mt-0.5" />
                    <span>Will set status to <b>{forceStatus}</b> without firing the gateway or changing the player&apos;s balance.</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setAction(null)} className="flex-1 h-10 border border-gray-200 rounded-sm text-sm">Cancel</button>
            <button onClick={confirmAction} className={`flex-1 h-10 text-white font-semibold rounded-sm text-sm ${action.status === "approved" ? (action.row.status === "failed" ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-500 hover:bg-emerald-600") : "bg-red-500 hover:bg-red-600"}`} data-testid="action-confirm">
              {action.status === "approved" ? (action.row.status === "failed" ? "Retry payout" : "Approve") : "Reject"}
            </button>
          </div>
        </Modal>
      )}

      {showCreate && (
        <Modal title="Manual withdrawal entry" onClose={() => setShowCreate(false)}>
          <form onSubmit={createManual} className="space-y-3">
            <Field label="Player Username" value={form.player_user_id} onChange={(v) => setForm({ ...form, player_user_id: v })} testId="create-username" required />
            <Field label="Amount (BDT)" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testId="create-amount" required />
            <Field label="Payment Method" value={form.payment_method} onChange={(v) => setForm({ ...form, payment_method: v })} testId="create-method" />
            <Field label="Transaction Reference" value={form.txn_ref} onChange={(v) => setForm({ ...form, txn_ref: v })} testId="create-txnref" />
            <Field label="Note" value={form.note} onChange={(v) => setForm({ ...form, note: v })} testId="create-note" />
            <div className="text-xs text-gray-500">Will immediately debit the player&apos;s balance.</div>
            <button type="submit" className="w-full h-10 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-sm text-sm" data-testid="create-submit">
              Debit player
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required }) {
  const isNumber = type === "number";
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">{label}</label>
      {isNumber ? (
        <NumberInput value={value} onChange={(raw) => onChange(raw)} asString required={required} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500" data-testid={testId} />
      ) : (
        <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required} className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500" data-testid={testId} />
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="font-display font-bold text-base">{title}</h3>
          <button onClick={onClose} className="h-8 w-8 flex items-center justify-center text-gray-500 hover:text-gray-900"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
